import React, { useState, useEffect } from 'react';
import Menu from '../main/menu';
import { auth, db, getEffectiveUserId } from '../firebase';
import { doc, getDoc, updateDoc, setDoc, writeBatch, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import './styles/Scheduler.css';
import { useSubscription } from '../contexts/SubscriptionContext';
import { format, startOfWeek, addDays, parse, isToday, isSameDay } from 'date-fns';
import { da } from 'date-fns/locale';
import { useCustomerSwitch } from '../contexts/CustomerSwitchContext';
import { useLanguage } from '../contexts/LanguageContext';

// Liste over tidszoner
const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/Copenhagen', label: 'Copenhagen (CET/CEST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Stockholm', label: 'Stockholm (CET/CEST)' },
  { value: 'Europe/Oslo', label: 'Oslo (CET/CEST)' },
  { value: 'Europe/Helsinki', label: 'Helsinki (EET/EEST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' }
];

const Scheduler = ({ onLogout }) => {
  const { weeklyPostsLimit, canCreateMorePosts, getRemainingWeeklyPosts } = useSubscription();
  const { selectedCustomer, isWorkingOnBehalfOfCustomer } = useCustomerSwitch();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [scheduleData, setScheduleData] = useState({
    deliveryDay: 'monday',
    deliveryTime: '09:00',
    timezone: 'UTC',
    weeklyPosts: 7,
    postSchedule: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    },
    autoScheduled: false
  });
  const [selectedDay, setSelectedDay] = useState(null);
  const [newTime, setNewTime] = useState('09:00');
  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [scheduledPostsCount, setScheduledPostsCount] = useState(0);
  const [remainingPosts, setRemainingPosts] = useState(0);
  const [deliveryDays, setDeliveryDays] = useState([]);
  const [deliveryTimes, setDeliveryTimes] = useState([]);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);

  useEffect(() => {
    const fetchScheduleData = async () => {
      try {
        if (!auth.currentUser) {
          console.error('Ingen bruger er logget ind');
          setLoading(false);
          return;
        }

        // Brug getEffectiveUserId for at få den korrekte bruger-ID
        const userId = getEffectiveUserId(selectedCustomer);
        
        if (!userId) {
          console.error("Ingen gyldig bruger-ID fundet");
          setLoading(false);
          return;
        }
        
        console.log("Henter planlæggerdata for bruger:", userId, selectedCustomer ? "Arbejder på vegne af kunde" : "Egen bruger");
        
        const userDoc = await getDoc(doc(db, 'users', userId));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Hent ugentlige opslag fra brugerindstillinger
          const weeklyPosts = userData.systems?.weeklyPosts || 0;
          
          // Hent planlæggerdata hvis de findes
          if (userData.schedule) {
            // Konverter gamle data til nyt format hvis nødvendigt
            let updatedSchedule = { ...userData.schedule };
            
            // Hvis det gamle format bruges (postDays og postTime), konvertér til nyt format
            if (updatedSchedule.postDays && !updatedSchedule.postSchedule) {
              const defaultTime = updatedSchedule.postTime || '09:00';
              const postSchedule = {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: []
              };
              
              // Konvertér gamle postDays til nyt postSchedule format
              updatedSchedule.postDays.forEach(day => {
                postSchedule[day] = [defaultTime];
              });
              
              updatedSchedule.postSchedule = postSchedule;
              delete updatedSchedule.postDays;
              delete updatedSchedule.postTime;
            }
            
            // Hvis tidszone ikke er defineret, brug UTC som standard
            if (!updatedSchedule.timezone) {
              updatedSchedule.timezone = 'UTC';
            }
            
            setScheduleData(updatedSchedule);
          }
        }
      } catch (error) {
        console.error('Fejl ved indlæsning af planlæggerdata:', error);
        setStatusMessage({
          type: 'error',
          message: t('scheduler', 'save.error')
        });
      } finally {
        setLoading(false);
      }
    };

    fetchScheduleData();
  }, [selectedCustomer, t]); // Tilføj selectedCustomer som afhængighed så vi opdaterer når kunden ændres

  useEffect(() => {
    const countScheduledPosts = async () => {
      try {
        // Brug getEffectiveUserId for at få den korrekte bruger-ID
        const effectiveUserId = getEffectiveUserId(selectedCustomer);
        
        if (!effectiveUserId) {
          console.error("Ingen gyldig bruger-ID fundet");
          return;
        }
        
        // Hent brugerens abonnement først
        const response = await fetch(
          `${process.env.REACT_APP_STRIPE_CHECK_SUBSCRIPTION_URL}/${effectiveUserId}?email=${encodeURIComponent(auth.currentUser.email)}`
        );
        
        if (!response.ok) {
          throw new Error('Kunne ikke hente abonnementsdata');
        }
        
        const data = await response.json();
        
        // Beregn start og slut på denne uge
        const now = new Date();
        const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
        startOfWeek.setHours(0, 0, 0, 0);
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        // Hent kun planlagte opslag for denne uge
        const postsRef = collection(db, 'users', effectiveUserId, 'scheduledPosts');
        const q = query(
          postsRef,
          where('scheduledFor', '>=', startOfWeek),
          where('scheduledFor', '<=', endOfWeek)
        );
        
        const querySnapshot = await getDocs(q);
        const thisWeeksPosts = querySnapshot.size;

        setScheduledPostsCount(thisWeeksPosts);
        setRemainingPosts(getRemainingWeeklyPosts(thisWeeksPosts));
        
      } catch (error) {
        console.error('Fejl ved optælling af planlagte opslag:', error);
      }
    };
    
    countScheduledPosts();
  }, [getRemainingWeeklyPosts, selectedCustomer]); // Tilføj selectedCustomer som afhængighed

  const handleDeliveryDayChange = (e) => {
    setScheduleData({ ...scheduleData, deliveryDay: e.target.value });
  };

  const handleDeliveryTimeChange = (e) => {
    setScheduleData({ ...scheduleData, deliveryTime: e.target.value });
  };

  const handleTimezoneChange = (e) => {
    setScheduleData({ ...scheduleData, timezone: e.target.value });
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setShowTimeModal(true);
  };

  const handleAddTimeToDay = async () => {
    if (!selectedDay || !newTime) return;
    
    // Tjek om brugeren kan planlægge flere opslag
    const canSchedule = await canCreateMorePosts(scheduledPostsCount);
    
    if (!canSchedule) {
      setStatusMessage({
        type: 'error',
        message: t('scheduler', 'limitWarning.used', { limit: weeklyPostsLimit })
      });
      
      // Nulstil statusbesked efter 3 sekunder
      setTimeout(() => {
        setStatusMessage({ type: '', message: '' });
      }, 3000);
      
      return;
    }
    
    // Tilføj ny tid til den valgte dag
    const updatedSchedule = { ...scheduleData };
    
    // Tjek om tiden allerede findes
    if (!updatedSchedule.postSchedule[selectedDay].includes(newTime)) {
      updatedSchedule.postSchedule[selectedDay] = [...updatedSchedule.postSchedule[selectedDay], newTime].sort();
      setScheduleData(updatedSchedule);
    }
    
    setShowTimeModal(false);
    
    // Efter vellykket planlægning, opdater tællerne
    setScheduledPostsCount(prev => prev + 1);
    setRemainingPosts(prev => prev - 1);
  };

  const handleRemoveTimeFromDay = (day, time) => {
    const updatedSchedule = { ...scheduleData };
    const index = updatedSchedule.postSchedule[day].indexOf(time);
    
    if (index !== -1) {
      updatedSchedule.postSchedule[day].splice(index, 1);
      setScheduleData(updatedSchedule);
    }
  };

  const handleNewTimeChange = (e) => {
    setNewTime(e.target.value);
  };

  const handleCloseModal = () => {
    setShowTimeModal(false);
    setSelectedDay(null);
  };

  const getTotalScheduledPosts = () => {
    return Object.values(scheduleData.postSchedule)
      .reduce((sum, times) => sum + times.length, 0);
  };

  const saveScheduleSettings = async () => {
    try {
      if (!auth.currentUser) {
        console.error('Ingen bruger er logget ind');
        return;
      }

      setStatusMessage({ type: '', message: '' });
      
      // Tjek at mindst ét tidspunkt er planlagt
      const totalScheduledTimes = getTotalScheduledPosts();
      
      if (totalScheduledTimes === 0) {
        setStatusMessage({
          type: 'error',
          message: 'Vælg mindst én dag og tidspunkt til at poste dine LinkedIn-opslag.'
        });
        return;
      }
      
      // Tjek om der er overskrides det maksimale antal ugentlige opslag
      if (totalScheduledTimes > weeklyPostsLimit) {
        setStatusMessage({
          type: 'error',
          message: `Du kan maksimalt planlægge ${weeklyPostsLimit} opslag per uge.`
        });
        return;
      }

      // Sikre at postSchedule altid er et objekt med arrays for hver dag
      const formattedScheduleData = {
        ...scheduleData,
        // Sikre at postSchedule altid er et objekt, aldrig en streng
        postSchedule: {
          monday: Array.isArray(scheduleData.postSchedule?.monday) ? scheduleData.postSchedule.monday : [],
          tuesday: Array.isArray(scheduleData.postSchedule?.tuesday) ? scheduleData.postSchedule.tuesday : [],
          wednesday: Array.isArray(scheduleData.postSchedule?.wednesday) ? scheduleData.postSchedule.wednesday : [],
          thursday: Array.isArray(scheduleData.postSchedule?.thursday) ? scheduleData.postSchedule.thursday : [],
          friday: Array.isArray(scheduleData.postSchedule?.friday) ? scheduleData.postSchedule.friday : [],
          saturday: Array.isArray(scheduleData.postSchedule?.saturday) ? scheduleData.postSchedule.saturday : [],
          sunday: Array.isArray(scheduleData.postSchedule?.sunday) ? scheduleData.postSchedule.sunday : []
        },
        // Sikre at timezone altid er sat (standard er UTC)
        timezone: scheduleData.timezone || 'UTC'
      };

      // Brug getEffectiveUserId for at få den korrekte bruger-ID
      const userId = getEffectiveUserId(selectedCustomer);
      
      if (!userId) {
        console.error("Ingen gyldig bruger-ID fundet");
        setStatusMessage({
          type: 'error',
          message: 'Kunne ikke finde gyldig bruger-ID.'
        });
        return;
      }
      
      console.log("Gemmer planlæggerdata for bruger:", userId, selectedCustomer ? "Arbejder på vegne af kunde" : "Egen bruger");
      
      // Gem i både brugerens hovedsamling og i settings/schedule
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        schedule: formattedScheduleData
      });
      
      // Gem også i settings/schedule
      const scheduleRef = doc(db, 'users', userId, 'settings', 'schedule');
      await setDoc(scheduleRef, formattedScheduleData);
      
      // TILFØJET: Gem i den nye schedules collection
      // Først slet eksisterende schedule-data for brugeren for at undgå dubletter
      const schedulesCollectionRef = collection(db, 'schedules');
      const existingSchedulesQuery = query(schedulesCollectionRef, where('userId', '==', userId));
      const existingSchedulesSnapshot = await getDocs(existingSchedulesQuery);
      
      // Slet eksisterende planlagte tider via batch
      const batch = writeBatch(db);
      existingSchedulesSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      // Opret dokument for ugentlig levering
      if (formattedScheduleData.deliveryDay && formattedScheduleData.deliveryTime) {
        const deliveryScheduleRef = doc(schedulesCollectionRef);
        batch.set(deliveryScheduleRef, {
          userId,
          type: 'delivery',
          day: formattedScheduleData.deliveryDay,
          time: formattedScheduleData.deliveryTime,
          timezone: formattedScheduleData.timezone, // Gem tidszone
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      // Opret dokumenter for deling på LinkedIn
      for (const [day, times] of Object.entries(formattedScheduleData.postSchedule)) {
        if (Array.isArray(times)) {
          for (const time of times) {
            const postScheduleRef = doc(schedulesCollectionRef);
            batch.set(postScheduleRef, {
              userId,
              type: 'posting',
              day,
              time,
              timezone: formattedScheduleData.timezone, // Gem tidszone
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        }
      }
      
      // Gem alle ændringer
      await batch.commit();
      console.log("Schedule-data gemt i den nye 'schedules' collection");
      
      setStatusMessage({
        type: 'success',
        message: t('scheduler', 'save.success')
      });

      // Nulstil statusbesked efter 3 sekunder
      setTimeout(() => {
        setStatusMessage({ type: '', message: '' });
      }, 3000);
    } catch (error) {
      console.error('Fejl ved gem af planlæggerindstillinger:', error);
      setStatusMessage({
        type: 'error',
        message: t('scheduler', 'save.error')
      });
    }
  };

  // Hjælpefunktion til at oversætte ugedage
  const translateDay = (day) => {
    return t('scheduler', `postSchedule.days.${day}`);
  };
  
  // Hjælpefunktion til at afgøre om en dag har planlagte opslag
  const getDayStatus = (day) => {
    if (!scheduleData.postSchedule[day] || scheduleData.postSchedule[day].length === 0) {
      return 'empty';
    }
    return 'scheduled';
  };

  const renderLimitWarning = () => {
    if (remainingPosts <= 0) {
      return (
        <div className="limit-warning">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{t('scheduler', 'limitWarning.used', { limit: weeklyPostsLimit })}</p>
          <a href="/linkedin-poster/subscribe" className="upgrade-button">{t('scheduler', 'limitWarning.upgrade')}</a>
        </div>
      );
    } else if (remainingPosts <= 2) {
      return (
        <div className="limit-warning low">
          <i className="fas fa-info-circle"></i>
          <p>{t('scheduler', 'limitWarning.low', { remaining: remainingPosts })}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-layout">
      <Menu onLogout={onLogout} />
      
      <main className="dashboard-content">
        {/* Vis banner, hvis man arbejder på vegne af kunde */}
        {isWorkingOnBehalfOfCustomer && (
          <div className="customer-action-banner">
            <i className="fas fa-user-friends"></i>
            <span>{t('scheduler', 'customerBanner', { customerName: selectedCustomer.name || selectedCustomer.email })}</span>
          </div>
        )}
      
        <div className="scheduler-header">
          <h1>{t('scheduler', 'header.title')}</h1>
          <div className="scheduler-limits-info">
            <span className="limit-badge">
              <i className="fas fa-calendar-check"></i>
              {t('scheduler', 'limits.scheduled', { count: scheduledPostsCount })}
            </span>
            <span className="limit-badge">
              <i className="fas fa-chart-pie"></i>
              {t('scheduler', 'limits.weekly', { limit: weeklyPostsLimit || 'Ubegrænset' })}
            </span>
            <span className="limit-badge">
              <i className="fas fa-hourglass-half"></i>
              {t('scheduler', 'limits.remaining', { count: remainingPosts })}
            </span>
          </div>
          <p className="scheduler-description">
            {t('scheduler', 'header.description')}
          </p>
          {renderLimitWarning()}
        </div>
        
        <div className={`scheduler-container mobile-adjusted ${isMenuCollapsed ? 'menu-collapsed' : ''}`}>
          {loading ? (
            <div className="scheduler-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <p>{t('scheduler', 'loading')}</p>
            </div>
          ) : (
            <div className="scheduler-container">
              <div className="scheduler-card">
                <div className="scheduler-card-header">
                  <h2>
                    <i className="fas fa-robot"></i>
                    {t('scheduler', 'botDelivery.title')}
                  </h2>
                  <p>{t('scheduler', 'botDelivery.description')}</p>
                </div>
                
                <div className="scheduler-card-content">
                  <div className="scheduler-form-group">
                    <label htmlFor="deliveryDay">{t('scheduler', 'botDelivery.day')}</label>
                    <select 
                      id="deliveryDay" 
                      value={scheduleData.deliveryDay}
                      onChange={handleDeliveryDayChange}
                      className="scheduler-select"
                    >
                      <option value="monday">{t('scheduler', 'postSchedule.days.monday')}</option>
                      <option value="tuesday">{t('scheduler', 'postSchedule.days.tuesday')}</option>
                      <option value="wednesday">{t('scheduler', 'postSchedule.days.wednesday')}</option>
                      <option value="thursday">{t('scheduler', 'postSchedule.days.thursday')}</option>
                      <option value="friday">{t('scheduler', 'postSchedule.days.friday')}</option>
                      <option value="saturday">{t('scheduler', 'postSchedule.days.saturday')}</option>
                      <option value="sunday">{t('scheduler', 'postSchedule.days.sunday')}</option>
                    </select>
                  </div>
                  
                  <div className="scheduler-form-group">
                    <label htmlFor="deliveryTime">{t('scheduler', 'botDelivery.time')}</label>
                    <input 
                      type="time" 
                      id="deliveryTime" 
                      value={scheduleData.deliveryTime}
                      onChange={handleDeliveryTimeChange}
                      className="scheduler-input"
                    />
                  </div>
                  
                  {/* Ny tidszone vælger */}
                  <div className="scheduler-form-group">
                    <label htmlFor="timezone">{t('scheduler', 'timezone.label')}</label>
                    <select 
                      id="timezone" 
                      value={scheduleData.timezone}
                      onChange={handleTimezoneChange}
                      className="scheduler-select"
                    >
                      {TIMEZONES.map(tz => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="scheduler-info">
                    <i className="fas fa-info-circle"></i>
                    <p>{t('scheduler', 'botDelivery.info', { day: translateDay(scheduleData.deliveryDay), time: scheduleData.deliveryTime })}</p>
                  </div>
                </div>
              </div>
              
              <div className="scheduler-card">
                <div className="scheduler-card-header">
                  <h2>
                    <i className="fab fa-linkedin"></i>
                    {t('scheduler', 'postSchedule.title')}
                  </h2>
                  <p>{t('scheduler', 'postSchedule.description')}</p>
                  <div className="scheduler-info">
                    <i className="fas fa-info-circle"></i>
                    <p><strong>Vigtigt:</strong> Disse tidspunkter bruges som standardplanlægning for nye opslag. Når du accepterer et opslag i AI Generator, får det automatisk tildelt det næste ledige tidspunkt fra disse indstillinger. Eksisterende planlagte opslag påvirkes ikke af ændringer her.</p>
                  </div>
                </div>
                
                <div className="scheduler-card-content">
                  <div className="calendar-view">
                    <div className="calendar-header">
                      <div className="day-header">{t('scheduler', 'postSchedule.days.monday')}</div>
                      <div className="day-header">{t('scheduler', 'postSchedule.days.tuesday')}</div>
                      <div className="day-header">{t('scheduler', 'postSchedule.days.wednesday')}</div>
                      <div className="day-header">{t('scheduler', 'postSchedule.days.thursday')}</div>
                      <div className="day-header">{t('scheduler', 'postSchedule.days.friday')}</div>
                      <div className="day-header">{t('scheduler', 'postSchedule.days.saturday')}</div>
                      <div className="day-header">{t('scheduler', 'postSchedule.days.sunday')}</div>
                    </div>
                    <div className="calendar-body">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                        <div 
                          key={day} 
                          className={`calendar-day ${getDayStatus(day)}`}
                          onClick={() => handleDayClick(day)}
                        >
                          <div className="day-name">{translateDay(day)}</div>
                          <div className="calendar-day-times-summary">
                            {scheduleData.postSchedule[day].length > 0 ? (
                              <>
                                <div className="times-count">
                                  <i className="far fa-clock"></i>
                                  <span>
                                    {scheduleData.postSchedule[day].length === 1 
                                      ? t('scheduler', 'postSchedule.onePosts') 
                                      : t('scheduler', 'postSchedule.multiplePosts', { count: scheduleData.postSchedule[day].length })}
                                  </span>
                                </div>
                                <div className="times-list">
                                  {scheduleData.postSchedule[day].map((time, index) => (
                                    <div key={index} className="time-chip">
                                      <span>{time}</span>
                                      <button
                                        className="remove-time-button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveTimeFromDay(day, time);
                                        }}
                                      >
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <div className="no-times">
                                <i className="fas fa-plus-circle"></i>
                                <span>{t('scheduler', 'postSchedule.addTime')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="post-summary">
                    <h3>{t('scheduler', 'summary.title')}</h3>
                    <p>{t('scheduler', 'summary.description')}</p>
                    <ul>
                      {Object.entries(scheduleData.postSchedule).map(([day, times]) => 
                        times.length > 0 && (
                          <li key={day}>
                            <strong>{translateDay(day)}:</strong> {times.join(', ')}
                          </li>
                        )
                      )}
                    </ul>
                    <p className="post-count-summary">
                      {t('scheduler', 'summary.totalPosts', { count: getTotalScheduledPosts(), limit: weeklyPostsLimit })}
                    </p>
                    <p className="timezone-summary">
                      {t('scheduler', 'timezone.info', { timezone: scheduleData.timezone })}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="scheduler-actions">
                <button 
                  className="scheduler-save-button"
                  onClick={saveScheduleSettings}
                >
                  <i className="fas fa-save"></i>
                  {t('scheduler', 'save.button')}
                </button>
              </div>
              
              {statusMessage.message && (
                <div className={`scheduler-status ${statusMessage.type}`}>
                  {statusMessage.type === 'success' ? (
                    <i className="fas fa-check-circle"></i>
                  ) : (
                    <i className="fas fa-exclamation-circle"></i>
                  )}
                  <p>{statusMessage.message}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      
      {/* Modal til at vælge tidspunkt */}
      {showTimeModal && (
        <div className="time-modal-overlay">
          <div className="time-modal">
            <div className="time-modal-header">
              <h3>{t('scheduler', 'timeModal.title', { day: translateDay(selectedDay) })}</h3>
              <button className="close-modal-button" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="time-modal-content">
              <div className="time-input-container">
                <label htmlFor="timeInput">{t('scheduler', 'timeModal.label')}</label>
                <input
                  id="timeInput"
                  type="time"
                  value={newTime}
                  onChange={handleNewTimeChange}
                  className="time-input"
                />
              </div>
              <div className="time-modal-info">
                <i className="fas fa-info-circle"></i>
                <p>{t('scheduler', 'timeModal.info')}</p>
              </div>
              <div className="time-modal-actions">
                <button className="cancel-button" onClick={handleCloseModal}>{t('scheduler', 'timeModal.cancel')}</button>
                <button className="add-time-button" onClick={handleAddTimeToDay}>
                  <i className="fas fa-plus"></i> {t('scheduler', 'timeModal.add')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Scheduler; 